/**
 * AppSidebar Component
 * Slim icon-based sidebar navigation. Logo and user menu moved to TopHeader.
 */

import { useState } from 'react';
import {
  Menu as MenuIcon,
  BarChart3,
  Users,
  Footprints,
  Settings,
  Home,
  HeartHandshake,
  MessageSquare,
  Landmark,
  Trophy,
  Database,
  Bot,
  ClipboardCheck,
  Target,
} from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { isVoteGoalOn } from '@/config/customerConfig';
import { useCampaignStore } from '@/stores';
import { useHasUnhealthyCalendarConnections } from '@/hooks/queries';
import { cn } from '@/lib/utils';
import { NavItem } from './NavItem';

// ============================================================================
// Constants
// ============================================================================

const SIDEBAR_WIDTH = 80; // Compact sidebar with icon + label

// ============================================================================
// Types
// ============================================================================

interface NavItemConfig {
  to: string;
  label: string;
  icon: React.ReactNode;
  visible: boolean;
  showBadge?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function AppSidebar() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isPlatformAdmin = user?.isPlatformAdmin ?? false;
  const canViewTeam = useCampaignStore((s) => s.canView('TeamManagement'));
  const canViewFinance = useCampaignStore((s) => s.canView('Finance'));
  const canManageFundraising = useCampaignStore((s) => s.canManage('Fundraising'));
  const isFullAdmin = useCampaignStore((s) => s.isFullAdmin());
  const hasUnhealthyConnections = useHasUnhealthyCalendarConnections();

  // Navigation items - consolidated structure
  const navItems: NavItemConfig[] = [
    { to: '/app/home', label: 'Home', icon: <Home />, visible: true },
    { to: '/app/vote-goal', label: 'Vote goal', icon: <Target />, visible: isVoteGoalOn() },
    { to: '/app/field-ops', label: 'Field Ops', icon: <Footprints />, visible: true },
    { to: '/app/analytics', label: 'Analytics', icon: <BarChart3 />, visible: true },
    { to: '/admin/staff', label: 'Staff', icon: <Users />, visible: isPlatformAdmin || canViewTeam },
    { to: '/admin/fundraising', label: 'Fundraising', icon: <HeartHandshake />, visible: isPlatformAdmin || canManageFundraising },
    { to: '/admin/finance', label: 'Finance', icon: <Landmark />, visible: isPlatformAdmin || canViewFinance },
    { to: '/admin/election-outcomes', label: 'Outcomes', icon: <Trophy />, visible: isPlatformAdmin },
    { to: '/admin/voter-data', label: 'Data queue', icon: <Database />, visible: isPlatformAdmin },
    { to: '/admin/assistant-telemetry', label: 'Assistant', icon: <Bot />, visible: isPlatformAdmin },
    { to: '/admin/messaging/profiles', label: '10DLC', icon: <ClipboardCheck />, visible: isPlatformAdmin },
    { to: '/app/work-orders', label: 'Orders', icon: <MessageSquare />, visible: isPlatformAdmin || isFullAdmin },
    { to: '/app/settings', label: 'Settings', icon: <Settings />, visible: true, showBadge: hasUnhealthyConnections },
  ];

  const visibleNavItems = navItems.filter((item) => item.visible);

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Sidebar content - icon-based navigation
  const sidebarContent = (
    <div
      className={cn(
        'h-full flex flex-col bg-sidebar-bg border-r border-sidebar-border',
        'transition-all duration-200'
      )}
      style={{ width: SIDEBAR_WIDTH }}
    >
      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto py-2 px-1">
        <div data-coachmark="sidebar-nav" className="space-y-0.5">
          {visibleNavItems.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              label={item.label}
              icon={item.icon}
              collapsed={false}
              showBadge={item.showBadge}
            />
          ))}
        </div>
      </nav>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger button - fixed top-left, hidden on desktop */}
      <button
        type="button"
        onClick={handleMobileToggle}
        aria-label="Open navigation"
        className="md:hidden fixed top-2 left-2 z-50 rounded-full bg-white p-2 shadow hover:bg-gray-100 transition-colors"
      >
        <MenuIcon className="h-5 w-5" />
      </button>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-20" style={{ width: SIDEBAR_WIDTH }}>
          {sidebarContent}
        </SheetContent>
      </Sheet>

      {/* Desktop: fixed sidebar, hidden on mobile */}
      <aside
        className="hidden md:block shrink-0 h-full"
        style={{ width: SIDEBAR_WIDTH }}
      >
        {sidebarContent}
      </aside>
    </>
  );
}

export default AppSidebar;
